/**
 * Blog slice (SCRUM-1952, RTK bundle 3) — the former switch reducer
 * blogReducer.js as createSlice. Behaviour-preserving: every case answers the
 * same legacy action type string (blogTypes.js) that blogActions.js already
 * dispatches, and returns the same state. The actions module is unchanged, so
 * no importer changes. `CLEAR_MESSAGE` is the same string designs and ad answer
 * to; each slice clears its own message, as the switch reducers did.
 * Characterised by tests/integration/reducers/contentReducers.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  ADD_BLOG,
  GET_BLOG,
  UPDATE_BLOG,
  ADD_POST,
  GET_POSTS,
  DELETE_POSTS,
  CHANGE_STATUS,
  GET_POST_BY_ID,
  UPDATE_POST,
  CLEAR_MESSAGE,
  DELETE_BLOG,
} from "./blogTypes";
import type { MessageResponse } from "../../../../shared/apiTypes";

type BlogState = {
  _id: string;
  title: string;
  description: string;
  posts: any[];
  blog: any;
  post_byid: any;
  message: string;
};

/** A write that answers with the server message, under its own action type. */
type MessageAction<T extends string> = PayloadAction<MessageResponse, T>;

const initialState: BlogState = {
  _id: "",
  title: "",
  description: "",
  posts: [],
  blog: {},
  post_byid: {},
  message: "",
};

/** The server message a write answers with. */
const storeMessage = (state: BlogState, action: { payload: MessageResponse }): BlogState => ({ ...state, message: action.payload.msg });

const blogSlice = createSlice({
  name: "blog",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(CLEAR_MESSAGE, (state) => ({ ...state, message: "" }))
      .addCase(ADD_BLOG, (state, action: PayloadAction<{ data: BlogState["blog"]; msg: string }, typeof ADD_BLOG>) => ({
        ...state,
        blog: action.payload.data,
        message: action.payload.msg,
      }))
      .addCase<typeof UPDATE_BLOG, MessageAction<typeof UPDATE_BLOG>>(UPDATE_BLOG, storeMessage)
      .addCase(GET_BLOG, (state, action: PayloadAction<BlogState["blog"], typeof GET_BLOG>) => {
        // GET api/blog returns a list, empty for a site that has no blog yet.
        // Indexing [0] unconditionally threw inside dispatch(), and getBlog's
        // catch reported that TypeError as "Failed to fetch blog" (SCRUM-948).
        const first = Array.isArray(action.payload) ? action.payload[0] : action.payload;
        return {
          ...state,
          blog: action.payload,
          _id: first?._id ?? "",
          title: first?.title ?? "",
          description: first?.description ?? "",
        };
      })
      .addCase(DELETE_BLOG, (state) => ({
        ...state,
        blog: {},
        _id: "",
        title: "",
        description: "",
      }))
      .addCase<typeof ADD_POST, MessageAction<typeof ADD_POST>>(ADD_POST, storeMessage)
      .addCase(GET_POSTS, (state, action: PayloadAction<BlogState["posts"], typeof GET_POSTS>) => ({ ...state, posts: action.payload }))
      .addCase(DELETE_POSTS, (state, action: PayloadAction<string, typeof DELETE_POSTS>) => ({
        ...state,
        posts: state.posts.filter((post) => post._id !== action.payload),
      }))
      .addCase(CHANGE_STATUS, (state, action: PayloadAction<{ element: BlogState["posts"][number] }, typeof CHANGE_STATUS>) => ({
        ...state,
        posts: state.posts.map((post) =>
          post._id === action.payload.element._id ? action.payload.element : post
        ),
      }))
      .addCase(GET_POST_BY_ID, (state, action: PayloadAction<BlogState["post_byid"], typeof GET_POST_BY_ID>) => ({ ...state, post_byid: action.payload }))
      .addCase<typeof UPDATE_POST, MessageAction<typeof UPDATE_POST>>(UPDATE_POST, storeMessage);
  },
});

export default blogSlice.reducer;
